import ReactGA from 'react-ga4';
import { auth } from '../config/config.ts';

export const GA4_MEASUREMENT_ID = 'G-7S62WCJ7YC';
const SIGNUP_KEY = 'ga4_pending_signup_v1';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
export type DispatchState = 'queued' | 'attempted' | 'tag_processed' | 'suppressed';

// Only acquisition parameters belong in analytics URLs. Never send checkout,
// email verification, password-reset tokens, or arbitrary application queries.
export function analyticsLocation(href: string): string {
  const url = new URL(href, window.location.origin);
  const allowed = new URLSearchParams();
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid'].forEach(key => {
    const value = url.searchParams.get(key);
    if (value) allowed.set(key, value.slice(0, 500));
  });
  return `${url.origin}${url.pathname}${allowed.toString() ? `?${allowed}` : ''}`;
}

// Snapshot synchronously, before React can remove a tagged landing URL. This
// stays in memory; no new acquisition cookie or cross-device identity is invented.
const entryLocation = typeof window === 'undefined' ? '' : analyticsLocation(window.location.href);
let initialized = false;
let lastPage = '';
const signupMemory = new Map<string, { uid: string; method: string; createdAt: number; state: DispatchState }>();

export function browserAnalyticsAllowed(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.doNotTrack !== '1' && !nav.globalPrivacyControl
    && !(window as any)[`ga-disable-${GA4_MEASUREMENT_ID}`];
}

function setTagIdentity(uid: string | null) {
  // Destination config overrides global `set` values. Clear the same scope on
  // logout, without generating another pageview or changing consent settings.
  ReactGA.gtag('config', GA4_MEASUREMENT_ID, { user_id: uid, send_page_view: false });
}

function ensureGA(): boolean {
  if (!browserAnalyticsAllowed()) return false;
  if (!initialized) {
    ReactGA.initialize(GA4_MEASUREMENT_ID, { gtagOptions: {
      send_page_view: false, page_location: entryLocation,
      user_id: auth?.currentUser?.uid || null,
    } });
    initialized = true;
    ReactGA.event('page_view', { page_location: entryLocation });
    lastPage = entryLocation;
  }
  return true;
}

export function trackGAPage(href: string) {
  try {
    if (!ensureGA()) return;
    const location = analyticsLocation(href);
    if (location !== lastPage) {
      ReactGA.event('page_view', { page_location: location });
      lastPage = location;
    }
  } catch { /* A later route/event may retry initialization. */ }
}

export function trackGAInteraction(name: string, params: Record<string, unknown>) {
  try {
    if (!ensureGA()) return;
    setTagIdentity(auth?.currentUser?.uid || null);
    ReactGA.event(name, { ...params, page_location: analyticsLocation(window.location.href) });
  } catch { /* Optional interaction tracking never interrupts the action. */ }
}

// Callback means the Google tag processed its command, NOT that GA4 collected
// it or Ads attributed it. Timeout is uncertain; never label it delivered.
export function dispatchGAEvent(
  name: string, params: Record<string, unknown>, uid: string, onProcessed?: () => void
): Promise<DispatchState> {
  return new Promise(resolve => {
    if (auth?.currentUser?.uid !== uid || !browserAnalyticsAllowed()) { resolve('suppressed'); return; }
    let settled = false;
    let acknowledged = false;
    const finish = (state: DispatchState) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(state);
    };
    const timer = setTimeout(() => finish('attempted'), 2000);
    try {
      if (!ensureGA()) { finish('suppressed'); return; }
      setTagIdentity(uid);
      ReactGA.event(name, { ...params, page_location: analyticsLocation(window.location.href),
        event_callback: () => {
          if (acknowledged) return;
          acknowledged = true;
          // Bookkeeping can still acknowledge this command after the caller's
          // timeout has resolved. It must not depend on settling the promise again.
          try { onProcessed?.(); } catch { /* Optional bookkeeping cannot break the tag callback. */ }
          finish('tag_processed');
        } });
    } catch { finish('queued'); }
  });
}

function persistSignups() {
  try { sessionStorage.setItem(SIGNUP_KEY, JSON.stringify([...signupMemory.values()])); } catch { /* Memory remains usable. */ }
}

export function syncGAIdentity() {
  try {
    const uid = auth?.currentUser?.uid || null;
    if (initialized) setTagIdentity(browserAnalyticsAllowed() ? uid : null);
    else if (uid) ensureGA();
    let saved: any[] = [];
    try { const value = JSON.parse(sessionStorage.getItem(SIGNUP_KEY) || '[]'); if (Array.isArray(value)) saved = value; } catch { /* Use memory. */ }
    for (const entry of saved) {
      if (entry?.uid && !signupMemory.has(entry.uid) && Date.now() - entry.createdAt < MAX_AGE_MS) signupMemory.set(entry.uid, entry);
    }
    for (const entry of signupMemory.values()) {
      if (Date.now() - entry.createdAt >= MAX_AGE_MS) { signupMemory.delete(entry.uid); continue; }
      if (entry.uid !== uid || entry.state !== 'queued') continue;
      entry.state = 'attempted';
      persistSignups();
      void dispatchGAEvent('sign_up', { method: entry.method }, entry.uid).then(state => {
        entry.state = state;
        persistSignups();
      });
    }
  } catch { /* Authentication never depends on analytics. */ }
}

export function trackGASignup(uid: string, method: 'email' | 'google') {
  if (!signupMemory.has(uid)) signupMemory.set(uid, { uid, method, createdAt: Date.now(), state: 'queued' });
  persistSignups();
  syncGAIdentity();
}
